import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Empty,
  Image,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CarOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import axios from "../../services/axiosInstance";
import {
  FETCH_SIGNED_DOCUMENTS,
  GET_RAKE_VEHICLE_UPLOADS,
} from "../../services/apiEndPoints";
import { IRakeVehicleUpload } from "../types/workflow.types";
import { getDocTypeConfig, isPdfUrl } from "./docTypeLabels";

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

interface VehicleUploadsModalProps {
  open: boolean;
  rakeMasterId: string | null;
  vehicleNumber: string | null;
  onClose: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  completed: "success",
  processing: "processing",
  pending: "default",
  failed: "error",
};

const VehicleUploadsModal = ({
  open,
  rakeMasterId,
  vehicleNumber,
  onClose,
}: VehicleUploadsModalProps) => {
  const [loading, setLoading] = useState(false);
  const [uploads, setUploads] = useState<IRakeVehicleUpload[]>([]);
  const [windowStart, setWindowStart] = useState<string | null>(null);
  const [windowEnd, setWindowEnd] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewRecord, setPreviewRecord] =
    useState<IRakeVehicleUpload | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchUploads = useCallback(async () => {
    if (!rakeMasterId || !vehicleNumber) return;
    setLoading(true);
    try {
      const res = await axios.get(
        GET_RAKE_VEHICLE_UPLOADS(rakeMasterId, vehicleNumber)
      );
      const data = res?.data?.data ?? res?.data;
      setUploads(Array.isArray(data?.uploads) ? data.uploads : []);
      setWindowStart(data?.windowStart ?? null);
      setWindowEnd(data?.windowEnd ?? null);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        message.error("Rake or vehicle not found");
      } else {
        message.error("Failed to load vehicle uploads");
      }
      setUploads([]);
    } finally {
      setLoading(false);
    }
  }, [rakeMasterId, vehicleNumber]);

  useEffect(() => {
    if (open) {
      fetchUploads();
    } else {
      setUploads([]);
      setWindowStart(null);
      setWindowEnd(null);
      setPreviewUrl(null);
      setPreviewRecord(null);
    }
  }, [open, fetchUploads]);

  const openPreview = useCallback(async (record: IRakeVehicleUpload) => {
    setPreviewRecord(record);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const res = await axios.get(
        `${FETCH_SIGNED_DOCUMENTS}?s3Url=${encodeURIComponent(record.s3Url)}`
      );
      const signed = res?.data?.results || res?.data?.data || record.s3Url;
      setPreviewUrl(signed);
    } catch {
      setPreviewUrl(record.s3Url);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewUrl(null);
    setPreviewRecord(null);
  }, []);

  const stats = useMemo(() => {
    const byType: Record<string, number> = {};
    uploads.forEach((u) => {
      byType[u.docType] = (byType[u.docType] || 0) + 1;
    });
    return { byType, total: uploads.length };
  }, [uploads]);

  const sortedUploads = useMemo(
    () =>
      [...uploads].sort(
        (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix()
      ),
    [uploads]
  );

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width="min(1100px, 95vw)"
        centered
        destroyOnClose
        title={
          vehicleNumber ? (
            <Space size={10} align="center">
              <CarOutlined style={{ color: "#1677ff", fontSize: 18 }} />
              <Text strong style={{ fontSize: 16 }}>
                {vehicleNumber}
              </Text>
              <Tag color="blue" style={{ margin: 0, borderRadius: 6 }}>
                {stats.total} upload{stats.total === 1 ? "" : "s"}
              </Tag>
            </Space>
          ) : (
            "Vehicle uploads"
          )
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin size="large" />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">Loading uploads...</Text>
            </div>
          </div>
        ) : (
          <>
            {(windowStart || windowEnd) && (
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  padding: "10px 14px",
                  marginBottom: 16,
                  fontSize: 12,
                  color: "#64748b",
                }}
              >
                <ClockCircleOutlined style={{ marginRight: 6 }} />
                Operation window:{" "}
                <Text strong style={{ fontSize: 12 }}>
                  {windowStart
                    ? dayjs(windowStart).format("MMM D, YYYY h:mm A")
                    : "--"}
                </Text>{" "}
                →{" "}
                <Text strong style={{ fontSize: 12 }}>
                  {windowEnd
                    ? dayjs(windowEnd).format("MMM D, YYYY h:mm A")
                    : "--"}
                </Text>
                {Object.keys(stats.byType).length > 0 && (
                  <Space wrap style={{ marginLeft: 12 }}>
                    {Object.entries(stats.byType).map(([t, c]) => {
                      const cfg = getDocTypeConfig(t);
                      return (
                        <Tag
                          key={t}
                          color={cfg.color}
                          style={{ margin: 0, borderRadius: 6, fontSize: 11 }}
                        >
                          {cfg.label}: {c}
                        </Tag>
                      );
                    })}
                  </Space>
                )}
              </div>
            )}

            {sortedUploads.length === 0 ? (
              <Empty
                description={`No uploads recorded for ${vehicleNumber || "this vehicle"} during the operation window`}
                style={{ padding: 40 }}
              />
            ) : (
              <Row gutter={[20, 16]}>
                <Col xs={24} md={10}>
                  <Title level={5} style={{ margin: "0 0 12px" }}>
                    Timeline
                  </Title>
                  <div
                    style={{
                      maxHeight: "60vh",
                      overflowY: "auto",
                      paddingRight: 8,
                    }}
                  >
                    <Timeline
                      mode="left"
                      items={sortedUploads.map((u) => {
                        const cfg = getDocTypeConfig(u.docType);
                        return {
                          color:
                            cfg.color === "default" ? "blue" : cfg.color,
                          children: (
                            <div
                              onClick={() => openPreview(u)}
                              style={{
                                cursor: "pointer",
                                padding: "8px 10px",
                                borderRadius: 8,
                                background: "#f8fafc",
                                border: "1px solid #f1f5f9",
                                transition: "all 0.15s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = "#1677ff";
                                e.currentTarget.style.background = "#eff6ff";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = "#f1f5f9";
                                e.currentTarget.style.background = "#f8fafc";
                              }}
                            >
                              <Space
                                size={6}
                                style={{ marginBottom: 4 }}
                                wrap
                              >
                                <Tag
                                  color={cfg.color}
                                  style={{
                                    margin: 0,
                                    borderRadius: 6,
                                    fontSize: 11,
                                  }}
                                >
                                  {cfg.label}
                                </Tag>
                                {u.status && (
                                  <Tag
                                    color={
                                      STATUS_COLORS[u.status] || "default"
                                    }
                                    style={{
                                      margin: 0,
                                      borderRadius: 6,
                                      fontSize: 11,
                                    }}
                                  >
                                    {u.status}
                                  </Tag>
                                )}
                              </Space>
                              <Text
                                style={{
                                  fontSize: 12,
                                  display: "block",
                                  color: "#1f2937",
                                }}
                              >
                                {dayjs(u.createdAt).format(
                                  "MMM D, YYYY · h:mm A"
                                )}
                              </Text>
                              <Text
                                type="secondary"
                                style={{ fontSize: 11 }}
                              >
                                {dayjs(u.createdAt).fromNow()}
                              </Text>
                            </div>
                          ),
                        };
                      })}
                    />
                  </div>
                </Col>

                <Col xs={24} md={14}>
                  <Title level={5} style={{ margin: "0 0 12px" }}>
                    Documents
                  </Title>
                  <div
                    style={{
                      maxHeight: "60vh",
                      overflowY: "auto",
                      paddingRight: 4,
                    }}
                  >
                    <Row gutter={[12, 12]}>
                      {sortedUploads.map((u) => {
                        const cfg = getDocTypeConfig(u.docType);
                        const pdf = isPdfUrl(u.s3Url);
                        return (
                          <Col xs={12} sm={8} key={u.workflowTaskId}>
                            <Card
                              hoverable
                              onClick={() => openPreview(u)}
                              styles={{ body: { padding: 0 } }}
                              style={{
                                borderRadius: 10,
                                overflow: "hidden",
                                border: "1px solid #e2e8f0",
                              }}
                            >
                              <div
                                style={{
                                  height: 130,
                                  background: "#f1f5f9",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  position: "relative",
                                  overflow: "hidden",
                                }}
                              >
                                {u.s3Url && !pdf ? (
                                  <img
                                    src={u.s3Url}
                                    alt={u.docType}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                    onError={(e) => {
                                      const img =
                                        e.target as HTMLImageElement;
                                      img.style.display = "none";
                                      const parent =
                                        img.parentElement as HTMLElement | null;
                                      if (parent) {
                                        parent.innerHTML =
                                          '<span style="color:#cbd5e1;font-size:32px">📄</span>';
                                      }
                                    }}
                                  />
                                ) : (
                                  <FileTextOutlined
                                    style={{
                                      fontSize: 36,
                                      color: "#cbd5e1",
                                    }}
                                  />
                                )}
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 6,
                                    right: 6,
                                    background: "rgba(15,23,42,0.7)",
                                    borderRadius: 6,
                                    padding: "2px 6px",
                                    color: "#fff",
                                    fontSize: 11,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <EyeOutlined /> View
                                </div>
                              </div>
                              <div style={{ padding: "10px 12px" }}>
                                <Tag
                                  color={cfg.color}
                                  style={{
                                    margin: 0,
                                    marginBottom: 6,
                                    borderRadius: 6,
                                    fontSize: 11,
                                  }}
                                >
                                  {cfg.label}
                                </Tag>
                                <Text
                                  style={{
                                    fontSize: 11,
                                    color: "#64748b",
                                    display: "block",
                                  }}
                                >
                                  {dayjs(u.createdAt).format(
                                    "MMM D · h:mm A"
                                  )}
                                </Text>
                              </div>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                </Col>
              </Row>
            )}
          </>
        )}
      </Modal>

      <Modal
        open={previewUrl !== null || previewLoading}
        onCancel={closePreview}
        footer={null}
        width="80vw"
        centered
        destroyOnClose
        zIndex={1100}
        title={
          previewRecord ? (
            <Space size={8}>
              <Text strong>{previewRecord.vehicleNumber}</Text>
              <Tag
                color={getDocTypeConfig(previewRecord.docType).color}
                style={{ margin: 0, borderRadius: 6 }}
              >
                {getDocTypeConfig(previewRecord.docType).label}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(previewRecord.createdAt).format(
                  "MMM D, YYYY · h:mm A"
                )}
              </Text>
              {previewUrl && (
                <Tooltip title="Open in new tab">
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button size="small" icon={<LinkOutlined />} />
                  </a>
                </Tooltip>
              )}
            </Space>
          ) : (
            "Preview"
          )
        }
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
            background: "#0f172a",
            borderRadius: 8,
          }}
        >
          {previewLoading || !previewUrl ? (
            <Spin />
          ) : isPdfUrl(previewUrl) ? (
            <iframe
              src={previewUrl}
              title="preview"
              style={{
                width: "100%",
                height: "70vh",
                border: "none",
                borderRadius: 8,
                background: "#fff",
              }}
            />
          ) : (
            <Image
              src={previewUrl}
              alt="preview"
              style={{ maxHeight: "70vh", objectFit: "contain" }}
              preview={false}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default VehicleUploadsModal;
